import { Injectable } from '@angular/core';
import {
  ExcursionInfoTableData,
  ExcursionInfoTableDataKeys,
} from '../../components/excursions-info-table/excursions-info-table.models';
import {
  AssociationKeys,
  Conference,
  ConferenceKeys,
  EmailAddressKeys,
  EmailTemplateConfiguration,
  EmailTemplateConfigurationKeys,
  EmailTemplates,
  ExcursionConfigurationsEmailKeys,
  ExcursionKeys,
  FlightInfoKeys,
  GuestData,
  GuestEvents,
  GuestKeys,
  GuestStatus,
  Headshot,
  HeadshotKeys,
  HotelReservationKeys,
  InviteeStatus,
  LocalDateTimeString,
  MediaSize,
  NotifyEmail,
  NotifyEmailKeys,
  RegistrantData,
  RegistrantKeys,
  RegistrantModelKeys,
  RegistrationSummaryEmail,
  RegistrationSummaryEmailExcursion,
  RegistrationSummaryEmailKeys,
  RegistrationSummaryEmailRegistrationDetail,
  RegistrationSummaryEmailTransaction,
  RequestOutcomeKeys,
  StripeTransaction,
  StripeTransactionKeys,
  TRANSACTIONS_STATUSES_LOOKUP,
} from '@ag-common-lib/public-api';
import { CloudFunctionsService } from '../cloud-functions.service';
import { WasabiImgPipe } from '../../../shared/pipes/wasabi-img.pipe';
import { ToastrService } from 'ngx-toastr';
import { ConferenceGuestsService } from '../conference-guests.service';
import { ConferenceTransactionsService } from '../conference-stripe-transactions.service';
import { ConferenceRegistrantsService } from '../conference-registrants/conference-registrants.service';
import { firstValueFrom, forkJoin, take } from 'rxjs';
import { SuffixPipe } from '../../../shared/pipes/suffix.pipe';
import { ConferenceService } from '../conference.service';
import { convertSelectedExcursionsToList, getExcursionsMap } from '../../utils/excursions-data.utils';
import { IsRequestedDifferentHotelCheckInCheckOutDatesPipe } from '../../../shared/pipes/is-requested-different-hotel-check-in-check-out-dates.pipe';
import { stringFromDate } from '../../utils/string-from-date';
import { groupBy, isEmpty } from 'lodash';
import { getFullAddress } from '../../../shared/utils/full-address';
import { format } from 'date-fns';
import { Options } from 'nodemailer/lib/mailer';
import { RegistrantChairmanExtensionKeys } from '@ag-common-lib/lib/models/registration/registrant-chairman-extension';
import { ChairmanExtensionConfigurationKeys } from '@ag-common-lib/lib/models/registration/conference-chairman-extension';
import { ConferencePossibleStayPeriodKey } from '@ag-common-lib/lib/models/registration/conference-stay-period.model';
import { getGuestRegistrationTypeDescription } from '../../utils/guest-registration-type-description.util';
import {
  getChairmanExtensionStayDates,
  isChairmanExtensionAccepted,
} from 'ag-common-svc/lib/utils/chailrman-extension-data.util';
export class SendRegistrationSummaryEmailPayload {
  conferenceDbId: string;
  registrantDbId: string;
  isTestEmail?: boolean;
  emailRecipientTestMode?: string[];
}

@Injectable()
export class RegistrationSummaryEmailService {
  constructor(
    private cloudFunctionsService: CloudFunctionsService,
    private wasabiImgPipe: WasabiImgPipe,
    private suffixPipe: SuffixPipe,
    private toastrService: ToastrService,
    private conferenceService: ConferenceService,
    private conferenceRegistrantsService: ConferenceRegistrantsService,
    private conferenceGuestsService: ConferenceGuestsService,
    private conferenceTransactionsService: ConferenceTransactionsService,
    private isRequestedDifferentHotelCheckInCheckOutDatesPipe: IsRequestedDifferentHotelCheckInCheckOutDatesPipe,
  ) {}

  async sendRegistrationSummaryEmail(
    emailTemplate: EmailTemplateConfiguration,
    payload: SendRegistrationSummaryEmailPayload,
  ): Promise<void> {
    const summaryEmail = await this.generateRegistrationSummaryEmail(emailTemplate, payload);

    await this.cloudFunctionsService
      .sendConferenceRegistrationSummary(summaryEmail)
      .then(() => {
        this.toastrService.success('Registration Summary was sent via email!');
      })
      .catch(error => {
        console.log('Email Error: ', error);

        this.toastrService.error('Registration Summary is not sent via email!');
      });
  }

  generateRegistrationSummaryEmail = async (
    emailTemplate: EmailTemplateConfiguration,
    { conferenceDbId, registrantDbId, isTestEmail, emailRecipientTestMode }: SendRegistrationSummaryEmailPayload,
  ): Promise<RegistrationSummaryEmail> => {
    const { registrant, guests, transactions } = await firstValueFrom(
      forkJoin({
        registrant: this.conferenceRegistrantsService.getDocumentData(conferenceDbId, registrantDbId).pipe(take(1)),
        guests: this.conferenceGuestsService.getList(conferenceDbId, registrantDbId).pipe(take(1)),
        transactions: this.conferenceTransactionsService.getList(conferenceDbId, registrantDbId).pipe(take(1)),
      }),
    );

    const registrantData = registrant?.[RegistrantModelKeys.data];
    if (!registrantData) {
      throw new Error('No registrant data found');
    }

    // Exclude removed/canceled guests from the summary email.
    const activeGuests =
      guests?.filter(guest => {
        const guestStatus = guest?.[GuestKeys.guestStatus];
        const canceledStatusesSet = new Set([GuestStatus.canceled, GuestStatus.deleted]);
        if (canceledStatusesSet.has(guestStatus)) {
          return false;
        }

        const guestEvents = guest?.[GuestKeys.guestEvents] ?? [GuestEvents.main];
        return !!guestEvents?.length;
      }) ?? [];

    const conference = await firstValueFrom(this.conferenceService.getDocumentData(conferenceDbId));
    const hotelReservation = registrantData?.[RegistrantKeys.hotelReservation];
    const excursions = await this.createRegistrationSummaryExcursionsRaw(
      conference,
      registrantData,
      activeGuests,
      true,
    );
    const isChairmanYes = isChairmanExtensionAccepted(registrantData, conference);
    const chairmanStayDates = isChairmanYes ? getChairmanExtensionStayDates(registrantData, conference) : null;
    const chairmanExcursions = isChairmanYes
      ? await this.createChairmanExtensionRegistrationSummaryExcursionsRaw(
          conference,
          registrantData,
          activeGuests,
          true,
        )
      : null;
    const checkInDate = hotelReservation?.[HotelReservationKeys.checkInDate];
    const checkOutDate = hotelReservation?.[HotelReservationKeys.checkOutDate];
    const requestedCheckInDate = hotelReservation?.[HotelReservationKeys.requestedCheckInDate];
    const requestedCheckOutDate = hotelReservation?.[HotelReservationKeys.requestedCheckOutDate];
    const hasAdditionalRoomRequest =
      hotelReservation?.[HotelReservationKeys.additionalRoomRequested] &&
      !hotelReservation?.[HotelReservationKeys.additionalRoomRequestOutcome]?.[RequestOutcomeKeys.state];
    const hasDifferBookingDatesRequest =
      this.isRequestedDifferentHotelCheckInCheckOutDatesPipe.transform(
        checkInDate,
        checkOutDate,
        requestedCheckInDate,
        requestedCheckOutDate,
      ) && !hotelReservation?.[HotelReservationKeys.requestDifferBookingDatesOutcome]?.[RequestOutcomeKeys.state];
    const eventStartDate = conference?.[ConferenceKeys.startDate];
    const eventEndDate = conference?.[ConferenceKeys.endDate];

    return {
      ...this.getCommonEmailData({
        emailTemplate,
        registrantData,
        conference,
        isTestEmail,
        emailRecipientTestMode,
      }),
      [RegistrationSummaryEmailKeys.flightItineraryEmail]: '',
      [RegistrationSummaryEmailKeys.eventStartDate]: stringFromDate(eventStartDate),
      [RegistrationSummaryEmailKeys.eventEndDate]: stringFromDate(eventEndDate),
      [RegistrationSummaryEmailKeys.eventName]: conference?.[ConferenceKeys.eventName] ?? 'conference',
      [RegistrationSummaryEmailKeys.hotelName]: conference?.[ConferenceKeys.hotelName] ?? 'Hyat Ziva Cap Cana',
      [RegistrationSummaryEmailKeys.hotelState]: conference?.[ConferenceKeys.hotelState] ?? 'Cap Cana',
      [RegistrationSummaryEmailKeys.coordinatorEmail]: isEmpty(conference?.[ConferenceKeys.eventCoordinatorEmail])
        ? 'events@alliancegrouplife.com'
        : conference?.[ConferenceKeys.eventCoordinatorEmail],
      [RegistrationSummaryEmailKeys.coordinatorPrimaryPhoneNumber]: isEmpty(
        conference?.[ConferenceKeys.eventCoordinatorPhone],
      )
        ? '8153389668'
        : conference?.[ConferenceKeys.eventCoordinatorPhone],
      [RegistrationSummaryEmailKeys.hasChairmanExtension]: isChairmanYes,
      [RegistrationSummaryEmailKeys.checkInDate]: stringFromDate(checkInDate),
      [RegistrationSummaryEmailKeys.checkOutDate]: stringFromDate(checkOutDate),
      [RegistrationSummaryEmailKeys.chairmanCheckInDate]: stringFromDate(
        chairmanStayDates?.[HotelReservationKeys.checkInDate],
      ),
      [RegistrationSummaryEmailKeys.chairmanCheckOutDate]: stringFromDate(
        chairmanStayDates?.[HotelReservationKeys.checkOutDate],
      ),
      [RegistrationSummaryEmailKeys.requestedCheckInDate]: stringFromDate(requestedCheckInDate),
      [RegistrationSummaryEmailKeys.requestedCheckOutDate]: stringFromDate(requestedCheckOutDate),
      [RegistrationSummaryEmailKeys.excursions]: excursions,
      [RegistrationSummaryEmailKeys.chairmanExcursions]: chairmanExcursions,
      [RegistrationSummaryEmailKeys.hasAdditionalRoomRequest]: hasAdditionalRoomRequest,
      [RegistrationSummaryEmailKeys.hasDifferBookingDatesRequest]: hasDifferBookingDatesRequest,
      [RegistrationSummaryEmailKeys.registrationDetails]: this.createRegistrationDetailSummaryEmail(
        registrantData,
        activeGuests,
      ),
      [RegistrationSummaryEmailKeys.transactions]: this.createTransactionsSummaryEmail(transactions),
      [RegistrationSummaryEmailKeys.facilityName]: conference?.[ConferenceKeys.facilityName],
    };
  };

  generateRegistrationBeforeYouGoEmail = async (
    emailTemplate: EmailTemplateConfiguration,
    { conferenceDbId, registrantDbId, isTestEmail, emailRecipientTestMode }: SendRegistrationSummaryEmailPayload,
  ): Promise<RegistrationSummaryEmail> => {
    const { registrant, guests, conference } = await firstValueFrom(
      forkJoin({
        registrant: this.conferenceRegistrantsService.getDocumentData(conferenceDbId, registrantDbId).pipe(take(1)),
        guests: this.conferenceGuestsService.getList(conferenceDbId, registrantDbId).pipe(take(1)),
        conference: this.conferenceService.getDocumentData(conferenceDbId).pipe(take(1)),
      }),
    );
    const registrantData = registrant?.[RegistrantModelKeys.data];

    const hotelReservation = registrantData?.[RegistrantKeys.hotelReservation];
    const checkInDate = hotelReservation?.[HotelReservationKeys.checkInDate];
    const checkOutDate = hotelReservation?.[HotelReservationKeys.checkOutDate];

    const flightInformation = registrantData?.[RegistrantKeys.flightInformation];
    const travelDateArrivalDate = flightInformation?.[FlightInfoKeys.arrivalDate] ?? checkInDate;
    const travelDateDepartureDate = flightInformation?.[FlightInfoKeys.departureDate] ?? checkOutDate;
    const eventStartDate = conference?.[ConferenceKeys.startDate];
    const eventEndDate = conference?.[ConferenceKeys.endDate];
    const hotelPhoneNumber = conference?.[ConferenceKeys.hotelPrimaryPhone];

    const activeGuests =
      guests?.filter(guest => {
        const guestStatus = guest?.[GuestKeys.guestStatus];
        const canceledStatusesSet = new Set([GuestStatus.canceled, GuestStatus.deleted]);
        if (canceledStatusesSet.has(guestStatus)) {
          return false;
        }

        const guestEvents = guest?.[GuestKeys.guestEvents] ?? [GuestEvents.main];
        return !!guestEvents?.length;
      }) ?? [];

    const excursions = await this.createRegistrationSummaryExcursionsRaw(conference, registrantData, activeGuests, true);
    const isChairmanYes = isChairmanExtensionAccepted(registrantData, conference);
    const chairmanStayDates = isChairmanYes ? getChairmanExtensionStayDates(registrantData, conference) : null;
    const chairmanExcursions = isChairmanYes
      ? await this.createChairmanExtensionRegistrationSummaryExcursionsRaw(
          conference,
          registrantData,
          activeGuests,
          true,
        )
      : null;

    return {
      ...this.getCommonEmailData({
        emailTemplate,
        registrantData,
        conference,
        isTestEmail,
        emailRecipientTestMode,
      }),
      [RegistrationSummaryEmailKeys.excursions]: excursions,
      [RegistrationSummaryEmailKeys.hasChairmanExtension]: isChairmanYes,
      [RegistrationSummaryEmailKeys.chairmanExcursions]: chairmanExcursions,
      [RegistrationSummaryEmailKeys.eventStartDate]: stringFromDate(eventStartDate),
      [RegistrationSummaryEmailKeys.eventEndDate]: stringFromDate(eventEndDate),
      [RegistrationSummaryEmailKeys.hotelPhoneNumber]: hotelPhoneNumber,
      [RegistrationSummaryEmailKeys.checkInDate]: stringFromDate(checkInDate),
      [RegistrationSummaryEmailKeys.checkOutDate]: stringFromDate(checkOutDate),
      [RegistrationSummaryEmailKeys.eventName]: conference?.[ConferenceKeys.eventName] ?? 'conference',
      [RegistrationSummaryEmailKeys.hotelName]: conference?.[ConferenceKeys.hotelName] ?? 'Hyat Ziva Cap Cana',
      [RegistrationSummaryEmailKeys.hotelState]: conference?.[ConferenceKeys.hotelState] ?? 'Cap Cana',
      [RegistrationSummaryEmailKeys.hotelAddress]: getFullAddress(conference?.[ConferenceKeys.hotelAddress]) ?? '-',
      [RegistrationSummaryEmailKeys.facilityName]: conference?.[ConferenceKeys.facilityName],
      [RegistrationSummaryEmailKeys.coordinatorEmail]: isEmpty(conference?.[ConferenceKeys.eventCoordinatorEmail])
        ? 'events@alliancegrouplife.com'
        : conference?.[ConferenceKeys.eventCoordinatorEmail],
      [RegistrationSummaryEmailKeys.coordinatorPrimaryPhoneNumber]: isEmpty(
        conference?.[ConferenceKeys.eventCoordinatorPhone],
      )
        ? '8153389668'
        : conference?.[ConferenceKeys.eventCoordinatorPhone],
      [RegistrationSummaryEmailKeys.travelDates]: `${stringFromDate(travelDateArrivalDate)} - ${stringFromDate(
        travelDateDepartureDate,
      )}`,
    };
  };

  generateActivitiesEmail = async (
    emailTemplate: EmailTemplateConfiguration,
    { conferenceDbId, registrantDbId, isTestEmail, emailRecipientTestMode }: SendRegistrationSummaryEmailPayload,
  ): Promise<any> => {
    const { registrant, guests, conference } = await firstValueFrom(
      forkJoin({
        registrant: this.conferenceRegistrantsService.getDocumentData(conferenceDbId, registrantDbId).pipe(take(1)),
        guests: this.conferenceGuestsService.getList(conferenceDbId, registrantDbId).pipe(take(1)),
        conference: this.conferenceService.getDocumentData(conferenceDbId).pipe(take(1)),
      }),
    );
    const conferenceExcursions = conference?.[ConferenceKeys.excursions] ?? [];
    const chairmanConferenceExcursions =
      conference?.[ConferenceKeys.chairmanExtensionConfiguration]?.[ChairmanExtensionConfigurationKeys.excursions] ??
      [];
    const excursionsGroupedByDate = groupBy(
      [...conferenceExcursions, ...chairmanConferenceExcursions].filter(Boolean),
      ExcursionKeys.date,
    );
    const activitiesDay = emailTemplate?.[EmailTemplateConfigurationKeys.payload]?.activitiesDay;
    const registrantData = registrant?.[RegistrantModelKeys.data];
    const excursions = await this.createRegistrationSummaryExcursionsRaw(conference, registrantData, guests);
    const isChairmanYes = isChairmanExtensionAccepted(registrantData, conference);
    const chairmanExcursions = isChairmanYes
      ? await this.createChairmanExtensionRegistrationSummaryExcursionsRaw(conference, registrantData, guests)
      : null;
    const activitiesForDay = excursionsGroupedByDate?.[activitiesDay];
    const formattedActivitiesDay = activitiesDay ? format(new Date(activitiesDay), 'EEEE') : null;
    // TODO add model
    return {
      ...this.getCommonEmailData({
        emailTemplate,
        registrantData,
        conference,
        isTestEmail,
        emailRecipientTestMode,
      }),
      [RegistrationSummaryEmailKeys.hasChairmanExtension]: isChairmanYes,
      [RegistrationSummaryEmailKeys.excursions]: excursions,
      [RegistrationSummaryEmailKeys.chairmanExcursions]: chairmanExcursions,
      activitiesForDay,
      formattedActivitiesDay,
    };
  };

  private getCommonEmailData = ({
    emailTemplate,
    registrantData,
    conference,
    isTestEmail = false,
    emailRecipientTestMode,
  }: {
    isTestEmail?: boolean;
    emailRecipientTestMode?: string[];
    emailTemplate: EmailTemplateConfiguration;
    registrantData: RegistrantData;
    conference: Conference;
  }) => {
    const contextCommonData: NotifyEmail = {
      [NotifyEmailKeys.imagePath]: null,
      [NotifyEmailKeys.firstName]: registrantData[RegistrantKeys.firstName],
      [NotifyEmailKeys.lastName]: registrantData[RegistrantKeys.lastName],
    };
    const summaryEmailTemplate =
      emailTemplate?.[EmailTemplateConfigurationKeys.templateName] ??
      EmailTemplates.notifyEmailConferenceRegistrationSummary;
    const subject = emailTemplate?.[EmailTemplateConfigurationKeys.subject];
    const attachments = emailTemplate?.[EmailTemplateConfigurationKeys.attachments];
    const eventCoordinatorEmail = conference?.[ConferenceKeys.eventCoordinatorEmail] ?? 'events@alliancegrouplife.com';

    contextCommonData[NotifyEmailKeys.summaryEmailTemplate] = summaryEmailTemplate;
    contextCommonData[NotifyEmailKeys.attachments] = attachments;
    contextCommonData[NotifyEmailKeys.replyTo] = eventCoordinatorEmail;

    const wasabiImagePath = conference?.[ConferenceKeys.welcomeImagePath] ?? null;

    if (wasabiImagePath) {
      const imgFullPath = this.getImageUrl(wasabiImagePath);

      contextCommonData[NotifyEmailKeys.imagePath] = imgFullPath;
    }

    const summaryEmail =
      registrantData?.[RegistrantKeys.primaryEmailAddress]?.[EmailAddressKeys.address] ??
      registrantData?.[RegistrantKeys.inviteeEmail];
    const hasDiffPrimaryEmail = summaryEmail !== registrantData[RegistrantKeys.inviteeEmail];
    const summaryCCEmail: string[] = [];
    if (hasDiffPrimaryEmail) {
      summaryCCEmail.push(registrantData[RegistrantKeys.inviteeEmail]);
    }
    if (registrantData[RegistrantKeys.ccEmail]) {
      summaryCCEmail.push(registrantData[RegistrantKeys.ccEmail]);
    }
    const bccEmail = conference?.[ConferenceKeys.bccEmail];

    if (isTestEmail) {
      let testModeSubject = `AG_MAILING_TEST ${subject} `;

      testModeSubject += `\n to ${summaryEmail}`;
      summaryCCEmail?.forEach(ccTo => {
        testModeSubject += `\n cc ${ccTo}`;
      });
      bccEmail?.forEach(ccTo => {
        testModeSubject += `\n bcc ${ccTo}`;
      });

      return Object.assign(contextCommonData, {
        [NotifyEmailKeys.email]: emailRecipientTestMode,
        [NotifyEmailKeys.subjectTitle]: testModeSubject,
      });
    }

    return Object.assign(contextCommonData, {
      [NotifyEmailKeys.email]: summaryEmail,
      [NotifyEmailKeys.subjectTitle]: subject,
      [NotifyEmailKeys.ccEmail]: summaryCCEmail,
      [NotifyEmailKeys.bccEmail]: bccEmail,
    });
  };

  private getImageUrl(wasabiImage: Headshot): string {
    const url = this.wasabiImgPipe.transform(wasabiImage?.wasabiPath, {
      mediaSize: MediaSize.origin,

      cropperPayload: wasabiImage?.[HeadshotKeys.imageCropperPayload] ?? null,
    });

    return url;
  }

  private createRegistrationSummaryExcursionsRaw = async (
    conference: Conference,
    registrantData: RegistrantData,
    guestsData: GuestData[],
    includePreferences = false,
  ): Promise<RegistrationSummaryEmailExcursion[]> => {
    const rows: RegistrationSummaryEmailExcursion[] = [];
    const registrantFullName = await this.getFullName({
      firstName: registrantData[RegistrantKeys.firstName],
      lastName: registrantData[RegistrantKeys.lastName],
      suffixId: registrantData[RegistrantKeys.suffix],
    });
    const excursions = conference?.[ConferenceKeys.excursions];
    const { excursionsMap } = getExcursionsMap(excursions);
    const registrantSelectedExcursions = registrantData?.[RegistrantKeys.selectedExcursions];
    const registrantExcursionsList = convertSelectedExcursionsToList(excursionsMap, registrantSelectedExcursions);

    const collect = (name: string, data: ExcursionInfoTableData) => {
      rows.push({
        [RegistrationSummaryEmailKeys.excursionAttendee]: name,
        [RegistrationSummaryEmailKeys.excursionName]: data?.[ExcursionInfoTableDataKeys.excursionName],
        [RegistrationSummaryEmailKeys.excursionDate]: data?.[ExcursionInfoTableDataKeys.excursionDate],
        selectedPreferences: includePreferences ? data?.[ExcursionInfoTableDataKeys.selectedPreferences] : null,
        configurations: data?.[ExcursionInfoTableDataKeys.configurations] ?? null,
      });
    };

    registrantExcursionsList.forEach(item => collect(registrantFullName, item));

    for (const guest of guestsData) {
      const guestFullName = await this.getFullName({
        firstName: guest[AssociationKeys.firstName],
        lastName: guest[AssociationKeys.lastName],
        suffixId: guest[AssociationKeys.suffix],
      });
      const guestSelectedExcursions = guest?.[GuestKeys.selectedExcursions];
      const guestExcursionsList = convertSelectedExcursionsToList(excursionsMap, guestSelectedExcursions);
      guestExcursionsList.forEach(item => collect(guestFullName, item));
    }

    if (!rows.length) {
      return null;
    }

    const toTime = (value: any) => (value ? new Date(value).getTime() : 0);
    rows.sort(
      (a, b) =>
        toTime(a?.[RegistrationSummaryEmailKeys.excursionDate]) -
        toTime(b?.[RegistrationSummaryEmailKeys.excursionDate]),
    );
    return rows;
  };

  private createChairmanExtensionRegistrationSummaryExcursionsRaw = async (
    conference: Conference,
    registrantData: RegistrantData,
    guestsData: GuestData[],
    includePreferences = false,
  ): Promise<RegistrationSummaryEmailExcursion[]> => {
    const rows: RegistrationSummaryEmailExcursion[] = [];
    const registrantFullName = await this.getFullName({
      firstName: registrantData[RegistrantKeys.firstName],
      lastName: registrantData[RegistrantKeys.lastName],
      suffixId: registrantData[RegistrantKeys.suffix],
    });

    const excursions =
      conference?.[ConferenceKeys.chairmanExtensionConfiguration]?.[ChairmanExtensionConfigurationKeys.excursions];
    const { excursionsMap } = getExcursionsMap(excursions);
    const registrantSelectedExcursions =
      registrantData?.[RegistrantKeys.chairmanExtension]?.[RegistrantChairmanExtensionKeys.selectedExcursions];
    const registrantExcursionsList = convertSelectedExcursionsToList(excursionsMap, registrantSelectedExcursions);

    const collect = (name: string, data: ExcursionInfoTableData) => {
      rows.push({
        [RegistrationSummaryEmailKeys.excursionAttendee]: name,
        [RegistrationSummaryEmailKeys.excursionName]: data?.[ExcursionInfoTableDataKeys.excursionName],
        [RegistrationSummaryEmailKeys.excursionDate]: data?.[ExcursionInfoTableDataKeys.excursionDate],
        selectedPreferences: includePreferences ? data?.[ExcursionInfoTableDataKeys.selectedPreferences] : null,
        configurations: data?.[ExcursionInfoTableDataKeys.configurations] ?? null,
      });
    };

    registrantExcursionsList.forEach(item => collect(registrantFullName, item));

    for (const guest of guestsData) {
      if (!guest?.[GuestKeys.guestEvents]?.includes(GuestEvents.chairmanExtension)) {
        continue;
      }
      const guestFullName = await this.getFullName({
        firstName: guest[AssociationKeys.firstName],
        lastName: guest[AssociationKeys.lastName],
        suffixId: guest[AssociationKeys.suffix],
      });
      const guestSelectedExcursions = guest?.[GuestKeys.chairmanExtSelectedExcursions];
      const guestExcursionsList = convertSelectedExcursionsToList(excursionsMap, guestSelectedExcursions);
      guestExcursionsList.forEach(item => collect(guestFullName, item));
    }

    if (!rows.length) {
      return null;
    }

    const toTime = (value: any) => (value ? new Date(value).getTime() : 0);
    rows.sort(
      (a, b) =>
        toTime(a?.[RegistrationSummaryEmailKeys.excursionDate]) -
        toTime(b?.[RegistrationSummaryEmailKeys.excursionDate]),
    );
    return rows;
  };

  private async getFullName({ firstName, lastName, suffixId }) {
    const suffix = await firstValueFrom(this.suffixPipe.transform(suffixId));
    return [firstName, lastName, suffix].filter(Boolean).join(' ');
  }

  private createTransactionsSummaryEmail = (
    transactions: StripeTransaction[],
  ): RegistrationSummaryEmailTransaction[] => {
    if (!transactions?.length) {
      return null;
    }
    const transactionsStatusesLookup = TRANSACTIONS_STATUSES_LOOKUP;
    return transactions.map(item => ({
      [RegistrationSummaryEmailKeys.transactionDate]: stringFromDate(item?.[StripeTransactionKeys.paymentDate]),
      [RegistrationSummaryEmailKeys.transactionTotal]: this.formatAmount(item?.[StripeTransactionKeys.amount]),
      [RegistrationSummaryEmailKeys.transactionStatus]: transactionsStatusesLookup.find(
        status => status.value === item?.[StripeTransactionKeys.status],
      )?.description,
    }));
  };

  private createRegistrationDetailSummaryEmail(
    registrantData: RegistrantData,
    guestsData: GuestData[],
  ): RegistrationSummaryEmailRegistrationDetail[] {
    const registrant = {
      [AssociationKeys.firstName]: registrantData?.[RegistrantKeys.firstName],
      [AssociationKeys.lastName]: registrantData?.[RegistrantKeys.lastName],
      [RegistrationSummaryEmailKeys.registrationType]: 'Qualifier',
    };
    const isApplicableForChairmanExtension = registrantData?.[RegistrantKeys.isApplicableForChairmanExtension];
    const guests = guestsData.map(guest => {
      return {
        [AssociationKeys.firstName]: guest?.[RegistrantKeys.firstName],
        [AssociationKeys.lastName]: guest?.[RegistrantKeys.lastName],
        [RegistrationSummaryEmailKeys.registrationType]: getGuestRegistrationTypeDescription({
          guestEvents: guest?.[GuestKeys.guestEvents],
          complimentaryConfiguration: guest?.[GuestKeys.complimentaryConfiguration],
          isApplicableForChairmanExtension,
        }),
      };
    });

    return [registrant, ...guests];
  }

  private formatAmount(number: number): string {
    return number?.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  static getMailOptions(context): Options {
    const mailOptions: Options = {
      to: context?.[NotifyEmailKeys.email],
      subject: context?.[NotifyEmailKeys.subjectTitle],
    };

    if (!!context?.[NotifyEmailKeys.replyTo]) {
      Object.assign(mailOptions, { replyTo: context?.[NotifyEmailKeys.replyTo] });
    }
    if (context?.[NotifyEmailKeys.ccEmail]?.length) {
      Object.assign(mailOptions, { cc: context?.[NotifyEmailKeys.ccEmail] });
    }
    if (context?.[NotifyEmailKeys.bccEmail]?.length) {
      Object.assign(mailOptions, { bcc: context?.[NotifyEmailKeys.bccEmail] });
    }
    return mailOptions;
  }
}
